import { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@/components/ui/modal'
import { useCreateUtilityType, useUpdateUtilityType } from '@/hooks/use-utilities'
import type { UtilityType, UtilityTypeTier } from '@riverpark/types'

interface UtilityTypeModalProps {
  open: boolean
  onClose: () => void
  editing?: UtilityType | null
}

const SELECT_CLS =
  'flex h-10 w-full rounded border border-input bg-card px-3.5 font-sans text-sm text-ink focus-visible:border-primary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-green/20'

const LABEL_CLS = 'font-sans text-sm font-medium text-ink'

interface TierFields {
  enabled: boolean
  baseRate: string
  standardCharge: string
  flatAmount: string
}

const EMPTY_TIER: TierFields = { enabled: false, baseRate: '', standardCharge: '', flatAmount: '' }

function tierFromExisting(tier: UtilityTypeTier | null): TierFields {
  if (!tier) return EMPTY_TIER
  return {
    enabled: true,
    baseRate: tier.baseRate != null ? String(tier.baseRate) : '',
    standardCharge: tier.standardCharge != null ? String(tier.standardCharge) : '',
    flatAmount: tier.flatAmount != null ? String(tier.flatAmount) : '',
  }
}

/** `null` when disabled (utility type doesn't bill this classification at all). */
function tierToPayload(
  tier: TierFields,
  billingMethod: 'meter_based' | 'flat_fee',
): { baseRate?: number; standardCharge?: number; flatAmount?: number } | null {
  if (!tier.enabled) return null
  return billingMethod === 'meter_based'
    ? {
        baseRate: parseFloat(tier.baseRate),
        standardCharge: tier.standardCharge ? parseFloat(tier.standardCharge) : 0,
      }
    : { flatAmount: parseFloat(tier.flatAmount) }
}

function TierFieldset({
  title,
  tier,
  onChange,
  billingMethod,
  unitLabel,
}: {
  title: string
  tier: TierFields
  onChange: (next: TierFields) => void
  billingMethod: 'meter_based' | 'flat_fee'
  unitLabel: string
}) {
  return (
    <div className="space-y-3 rounded border border-border-2 p-3">
      <label className="flex items-center gap-2 font-sans text-sm font-medium text-ink">
        <input
          type="checkbox"
          checked={tier.enabled}
          onChange={(e) => onChange({ ...tier, enabled: e.target.checked })}
        />
        Bill {title}
      </label>
      {tier.enabled &&
        (billingMethod === 'meter_based' ? (
          <>
            <Input
              label="Rate per unit (KES)"
              type="number"
              min="0"
              step="0.01"
              value={tier.baseRate}
              onChange={(e) => onChange({ ...tier, baseRate: e.target.value })}
              placeholder="e.g. 50"
              hint={unitLabel ? `Per ${unitLabel}` : undefined}
              required
            />
            <Input
              label="Standard charge (KES)"
              hint="Optional — added on top of consumption every period"
              type="number"
              min="0"
              step="0.01"
              value={tier.standardCharge}
              onChange={(e) => onChange({ ...tier, standardCharge: e.target.value })}
              placeholder="e.g. 250"
            />
          </>
        ) : (
          <Input
            label="Flat amount per plot (KES)"
            type="number"
            min="0"
            step="0.01"
            value={tier.flatAmount}
            onChange={(e) => onChange({ ...tier, flatAmount: e.target.value })}
            placeholder="e.g. 2500"
            required
          />
        ))}
    </div>
  )
}

export function UtilityTypeModal({ open, onClose, editing }: UtilityTypeModalProps) {
  const create = useCreateUtilityType()
  const update = useUpdateUtilityType()

  const [name, setName] = useState('')
  const [invoicePrefix, setInvoicePrefix] = useState('')
  const [description, setDescription] = useState('')
  const [billingMethod, setBillingMethod] = useState<'meter_based' | 'flat_fee'>('flat_fee')
  const [developerTier, setDeveloperTier] = useState<TierFields>(EMPTY_TIER)
  const [ownerTier, setOwnerTier] = useState<TierFields>(EMPTY_TIER)
  const [unitLabel, setUnitLabel] = useState('')
  const [frequency, setFrequency] = useState<'monthly' | 'quarterly' | 'annually'>('monthly')
  const [generationMode, setGenerationMode] = useState<'manual' | 'auto'>('manual')
  const [billingDay, setBillingDay] = useState(1)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    if (editing) {
      setName(editing.name)
      setInvoicePrefix(editing.invoicePrefix)
      setDescription(editing.description ?? '')
      setBillingMethod(editing.billingMethod)
      setDeveloperTier(tierFromExisting(editing.pricing.developer))
      setOwnerTier(tierFromExisting(editing.pricing.owner))
      setUnitLabel(editing.unitLabel ?? '')
      setFrequency(editing.frequency)
      setGenerationMode(editing.generationMode)
      setBillingDay(editing.billingDay)
    } else {
      setName('')
      setInvoicePrefix('')
      setDescription('')
      setBillingMethod('flat_fee')
      setDeveloperTier(EMPTY_TIER)
      setOwnerTier(EMPTY_TIER)
      setUnitLabel('')
      setFrequency('monthly')
      setGenerationMode('manual')
      setBillingDay(1)
    }
    setError(null)
  }, [open, editing])

  // auto-set generationMode to manual when switching to meter_based
  function handleBillingMethodChange(m: 'meter_based' | 'flat_fee') {
    setBillingMethod(m)
    if (m === 'meter_based') setGenerationMode('manual')
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (!developerTier.enabled && !ownerTier.enabled) {
      setError('Enable pricing for at least one of Developer or Owner')
      return
    }
    const pricing = {
      developer: tierToPayload(developerTier, billingMethod),
      owner: tierToPayload(ownerTier, billingMethod),
    }
    try {
      if (editing) {
        await update.mutateAsync({
          id: editing.id,
          body: {
            name,
            invoicePrefix,
            ...(description && { description }),
            ...(billingMethod === 'meter_based' && { unitLabel }),
            pricing,
            frequency,
            generationMode,
            billingDay,
          },
        })
      } else {
        await create.mutateAsync({
          name,
          invoicePrefix,
          ...(description && { description }),
          billingMethod,
          ...(billingMethod === 'meter_based' && { unitLabel }),
          pricing,
          frequency,
          generationMode,
          billingDay,
        })
      }
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong')
    }
  }

  const isPending = create.isPending || update.isPending

  return (
    <Modal open={open} onClose={onClose} labelledBy="utility-modal-title">
      <ModalHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-sans text-label uppercase tracking-[0.12em] text-muted-foreground">
              Utilities
            </p>
            <h2 id="utility-modal-title" className="font-serif text-xl text-ink">
              {editing ? 'Edit utility type' : 'New utility type'}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-ink"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
      </ModalHeader>

      <form onSubmit={(e) => void handleSubmit(e)} className="flex min-h-0 flex-1 flex-col">
        <ModalBody className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-[1fr_auto]">
            <Input
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Borehole Water"
              required
            />
            <div className="space-y-1.5">
              <label className={LABEL_CLS} htmlFor="ut-prefix">
                Invoice prefix
              </label>
              <input
                id="ut-prefix"
                value={invoicePrefix}
                onChange={(e) => setInvoicePrefix(e.target.value.toUpperCase().slice(0, 4))}
                placeholder="BH"
                maxLength={4}
                required
                className="flex h-10 w-24 rounded border border-input bg-card px-3.5 font-mono text-sm font-medium uppercase tracking-widest text-ink focus-visible:border-primary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-green/20"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className={LABEL_CLS} htmlFor="ut-description">
              Description <span className="font-normal text-muted-foreground">(optional)</span>
            </label>
            <textarea
              id="ut-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              maxLength={500}
              className="w-full rounded border border-input bg-card px-3.5 py-2.5 font-sans text-sm text-ink placeholder:text-muted-foreground focus-visible:border-primary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-green/20"
            />
          </div>

          {/* Billing method — locked when editing */}
          <div className="space-y-1.5">
            <label className={LABEL_CLS} htmlFor="ut-method">
              Billing method
            </label>
            <select
              id="ut-method"
              value={billingMethod}
              onChange={(e) =>
                handleBillingMethodChange(e.target.value as 'meter_based' | 'flat_fee')
              }
              disabled={!!editing}
              className={SELECT_CLS}
            >
              <option value="flat_fee">Flat fee — fixed amount per plot</option>
              <option value="meter_based">Meter-based — charged per unit consumed</option>
            </select>
            {editing && (
              <p className="font-sans text-xs text-muted-foreground">
                Billing method cannot be changed after creation.
              </p>
            )}
          </div>

          {billingMethod === 'meter_based' && (
            <Input
              label="Unit label"
              value={unitLabel}
              onChange={(e) => setUnitLabel(e.target.value)}
              placeholder="e.g. m³"
              required
            />
          )}

          {/* Per-classification pricing — a plot's Developer/Owner status determines which tier
              (if any) it's billed under. Disabling a tier means that classification is never
              billed by this utility type at all. */}
          <div className="space-y-1.5">
            <span className={LABEL_CLS}>Pricing</span>
            <div className="grid gap-3 sm:grid-cols-2">
              <TierFieldset
                title="Developers"
                tier={developerTier}
                onChange={setDeveloperTier}
                billingMethod={billingMethod}
                unitLabel={unitLabel}
              />
              <TierFieldset
                title="Owners"
                tier={ownerTier}
                onChange={setOwnerTier}
                billingMethod={billingMethod}
                unitLabel={unitLabel}
              />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <label className={LABEL_CLS} htmlFor="ut-frequency">
                Billing frequency
              </label>
              <select
                id="ut-frequency"
                value={frequency}
                onChange={(e) => setFrequency(e.target.value as typeof frequency)}
                className={SELECT_CLS}
              >
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="annually">Annually</option>
              </select>
            </div>

            <div className="space-y-1.5">
              <label className={LABEL_CLS} htmlFor="ut-mode">
                Generation mode
              </label>
              <select
                id="ut-mode"
                value={generationMode}
                onChange={(e) => setGenerationMode(e.target.value as typeof generationMode)}
                disabled={billingMethod === 'meter_based'}
                className={SELECT_CLS}
              >
                <option value="manual">Manual — triggered by staff</option>
                <option value="auto">Auto — runs on schedule</option>
              </select>
              {billingMethod === 'meter_based' && (
                <p className="font-sans text-xs text-muted-foreground">
                  Meter-based utilities are always manual (readings must be entered first).
                </p>
              )}
            </div>
          </div>

          {/* Billing day — only relevant for auto-generated utilities */}
          {generationMode === 'auto' && (
            <div className="space-y-1.5">
              <label className={LABEL_CLS} htmlFor="ut-billing-day">
                Billing day
              </label>
              <select
                id="ut-billing-day"
                value={billingDay}
                onChange={(e) => setBillingDay(Number(e.target.value))}
                className={SELECT_CLS}
              >
                {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                  <option key={d} value={d}>
                    {d === 1 ? '1st' : d === 2 ? '2nd' : d === 3 ? '3rd' : `${d}th`} of the month
                  </option>
                ))}
              </select>
              <p className="font-sans text-xs text-muted-foreground">
                {frequency === 'monthly' && 'Invoices generate on this day every month.'}
                {frequency === 'quarterly' &&
                  'Invoices generate on this day in Jan, Apr, Jul & Oct.'}
                {frequency === 'annually' && 'Invoices generate on this day in January each year.'}
              </p>
            </div>
          )}

          {error && (
            <p className="font-sans text-sm text-destructive" role="alert">
              {error}
            </p>
          )}
        </ModalBody>

        <ModalFooter>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={isPending}>
            {editing ? 'Save changes' : 'Create utility type'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  )
}
